import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InProgressOrders from './order_management/InProgressOrders';
import ProcessedInstructions from './order_management/ProcessedInstructions';
import CalculatedOrders from './order_management/CalculatedOrders';
import BlockCalculatedOrders from './order_management/BlockCalculatedOrders';
import CompletedOrders from './order_management/CompletedOrders';
import { LocationState } from './orders/types';
import OrdersPageLayout, { OrderTab, OrderActionButton } from './orders/OrdersPageLayout';

type Tab = 'inprogress' | 'processed' | 'calculated' | 'blockcalculated' | 'completed';

const TABS: OrderTab[] = [
    { key: 'inprogress', label: 'In-Progress' },
    { key: 'processed', label: 'Processed Instructions' },
    { key: 'calculated', label: 'Calculated Orders' },
    { key: 'blockcalculated', label: 'Block Calculated' },
    { key: 'completed', label: 'Completed' },
];

const OrdersList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { message, orderSubmitted, activeTab: initialActiveTab } = (location.state as LocationState) || {};

    const [activeTab, setActiveTab] = useState<Tab>(
        initialActiveTab || (orderSubmitted ? 'calculated' : 'inprogress'),
    );
    const [successMessage, setSuccessMessage] = useState<string | null>(message || null);

    useEffect(() => {
        if (initialActiveTab) {
            setActiveTab(initialActiveTab as Tab);
        } else if (orderSubmitted) {
            setActiveTab('calculated');
        }
    }, [orderSubmitted, initialActiveTab]);

    useEffect(() => {
        if (!successMessage) return;
        const timer = setTimeout(() => setSuccessMessage(null), 5000);
        return () => clearTimeout(timer);
    }, [successMessage]);

    const handleProcessSuccess = (blockTrade: boolean) => {
        setSuccessMessage('Orders processed successfully');
        setActiveTab(blockTrade ? 'blockcalculated' : 'calculated');
    };

    const handleDeleteSuccess = () => {
        setSuccessMessage('Instructions deleted successfully');
        setActiveTab('processed');
    };

    const handleOrdersSubmitted = () => {
        setSuccessMessage('Orders submitted successfully');
        setActiveTab('completed');
    };

    const actionButtons: OrderActionButton[] = [
        { label: 'Create Buy Order', onClick: () => navigate('/orders/buy/new'), variant: 'buy' },
        { label: 'Create Sell Order', onClick: () => navigate('/orders/sell/new'), variant: 'sell' },
    ];

    return (
        <OrdersPageLayout
            title="Orders"
            subtitle="Instructions through to submitted orders, one stage per tab."
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as Tab)}
            actionButtons={actionButtons}
            successMessage={successMessage}
        >
            {activeTab === 'inprogress' && <InProgressOrders onProcessSuccess={handleProcessSuccess} />}
            {activeTab === 'processed' && <ProcessedInstructions onDeleteSuccess={handleDeleteSuccess} />}
            {activeTab === 'calculated' && <CalculatedOrders onOrdersSubmitted={handleOrdersSubmitted} />}
            {activeTab === 'blockcalculated' && <BlockCalculatedOrders />}
            {activeTab === 'completed' && <CompletedOrders />}
        </OrdersPageLayout>
    );
};

export default OrdersList;